import React, { useState, useEffect } from 'react';
import { Table, Card, Select, DatePicker, Input, Space, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { RangePicker } = DatePicker;

const userTypeMap = {
  1: { text: '管理员', color: 'blue' },
  2: { text: '门店管理员', color: 'green' },
  3: { text: '部门管理员', color: 'orange' },
};

const actionMap = {
  CREATE: { text: '创建', color: 'green' },
  UPDATE: { text: '修改', color: 'blue' },
  DELETE: { text: '删除', color: 'red' },
  LOGIN: { text: '登录', color: 'purple' },
};

const moduleMap = {
  products: '货品管理',
  batches: '批次管理',
  stores: '门店管理',
  store_admins: '门店账户',
  departments: '部门管理',
  department_admins: '部门账户',
  jade_types: '料种管理',
  ys7_devices: '监控设备',
  upload: '文件上传',
  config: '系统配置',
  photo_type_descriptions: '图片常用语',
  customers: '客户管理',
  store_products: '门店货品',
  department_batches: '部门批次',
  admin: '管理员',
  store_auth: '门店认证',
  department_auth: '部门认证',
  audit_logs: '系统日志',
};

const AuditLogs = () => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_type: undefined,
    action: undefined,
    module: undefined,
    dateRange: null,
    keyword: '',
  });

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.pageSize,
      };
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.action) params.action = filters.action;
      if (filters.module) params.module = filters.module;
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.dateRange && filters.dateRange[0]) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const res = await api.auditLog.list(params);
      if (res.success) {
        setData(res.data.list);
        setPagination({
          ...pagination,
          current: res.data.pagination.current,
          total: res.data.pagination.total,
        });
      }
    } catch (error) {
      console.error('获取日志失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData(1);
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '用户类型',
      dataIndex: 'user_type',
      key: 'user_type',
      width: 110,
      render: (type) => {
        const info = userTypeMap[type] || { text: '未知', color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (action) => {
        const info = actionMap[action] || { text: action, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 110,
      render: (mod) => moduleMap[mod] || mod,
    },
    {
      title: '详情',
      dataIndex: 'detail',
      key: 'detail',
      ellipsis: true,
    },
    {
      title: '对象ID',
      dataIndex: 'target_id',
      key: 'target_id',
      width: 80,
      render: (text) => text || '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: (text) => text || '-',
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="用户类型"
            allowClear
            style={{ width: 130 }}
            value={filters.user_type}
            onChange={(val) => setFilters({ ...filters, user_type: val })}
            options={[
              { label: '管理员', value: 1 },
              { label: '门店管理员', value: 2 },
              { label: '部门管理员', value: 3 },
            ]}
          />
          <Select
            placeholder="操作类型"
            allowClear
            style={{ width: 100 }}
            value={filters.action}
            onChange={(val) => setFilters({ ...filters, action: val })}
            options={[
              { label: '创建', value: 'CREATE' },
              { label: '修改', value: 'UPDATE' },
              { label: '删除', value: 'DELETE' },
              { label: '登录', value: 'LOGIN' },
            ]}
          />
          <Select
            placeholder="模块"
            allowClear
            style={{ width: 130 }}
            value={filters.module}
            onChange={(val) => setFilters({ ...filters, module: val })}
            options={Object.entries(moduleMap).map(([value, label]) => ({ label, value }))}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Input
            placeholder="搜索用户名/详情"
            prefix={<SearchOutlined />}
            style={{ width: 180 }}
            value={filters.keyword}
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            onPressEnter={handleSearch}
          />
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onChange={(p) => fetchData(p.current)}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default AuditLogs;
